import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WaitlistModal from '../components/WaitlistModal';
import { useTextUnderline } from '../hooks/useTextUnderline';
import { useAnimateOnScroll } from '../hooks/useAnimateOnScroll';

function NebulaCTA({ onOpenWaitlist, onOpenRoles }) {
  const containerRef = useRef(null);
  const systemRef = useRef(null);
  const followerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const system = systemRef.current;
    const follower = followerRef.current;
    if (!container || !system || !follower) return;

    let mouseX = 0, mouseY = 0, currentX = 0, currentY = 0;
    let rafId;
    const factor = 0.05;

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };
    const onEnter = () => { system.style.opacity = '1'; };
    const onLeave = () => { system.style.opacity = '0'; };

    function animate() {
      currentX += (mouseX - currentX) * factor;
      currentY += (mouseY - currentY) * factor;
      follower.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      rafId = requestAnimationFrame(animate);
    }

    container.addEventListener('mousemove', onMove);
    container.addEventListener('mouseenter', onEnter);
    container.addEventListener('mouseleave', onLeave);
    animate();

    return () => {
      container.removeEventListener('mousemove', onMove);
      container.removeEventListener('mouseenter', onEnter);
      container.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section className="section-join" id="join">
      <div className="container-large">
        <div className="join-inner" ref={containerRef}>
          <div className="nebula-system-company" ref={systemRef} style={{ opacity: 0 }}>
            <div className="nebula-follower-company" ref={followerRef}>
              <div className="nebula-glow-layer layer-1-company"></div>
              <div className="nebula-glow-layer layer-2-company"></div>
              <div className="nebula-glow-layer layer-3-company"></div>
            </div>
          </div>
          <div className="join-text">
            <h2>Come <span className="highlight-yellow js-text-underline" data-underline="sm">build with us</span></h2>
            <p>We're looking for talented engineers, designers, and operators who believe technology can transform lives. If Afghanistan's financial future excites you, we want to hear from you.</p>
          </div>
          <div className="join-actions">
            <a className="m-button__wrap" onClick={(e) => { e.preventDefault(); }} data-btn="up" href="#!">
              <span className="m-b-bg-white" aria-hidden="true"></span>
              <span className="m-b-text" aria-hidden="true">
                <span className="m-b-text-static">View Open Roles</span>
                <span className="m-b-text-up">View Open Roles</span>
              </span>
              <span className="m-b-arrow" aria-hidden="true">
                <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
              </span>
              <span className="sr-only">View Open Roles</span>
            </a>
            <a id="getEarlyAccessBtn" className="m-button__wrap" onClick={(e) => { e.preventDefault(); onOpenWaitlist(); }} data-btn="up" href="#!">
              <span className="m-b-bg-yellow" aria-hidden="true"></span>
              <span className="m-b-text" aria-hidden="true">
                <span className="m-b-text-static">Get Early Access</span>
                <span className="m-b-text-up">Get Early Access</span>
              </span>
              <span className="m-b-arrow" aria-hidden="true">
                <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
              </span>
              <span className="sr-only">Get Early Access</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function CompanyPage() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  useTextUnderline();
  useAnimateOnScroll();

  useEffect(() => { document.title = 'Chattak | Company - Our Mission to Digitize Afghanistan'; }, []);

  const companyNavLinks = (
    <>
      <a href="/#payment-grid">Product</a>
      <a href="/#api">Developers</a>
      <Link to="/company" className="active">Company</Link>
      <Link to="/login" className="login-btn">Log in</Link>
    </>
  );

  return (
    <>
      <nav>
        <div className="nav-container">
          <div className="logo-wrap">
            <div className="logo-symbol">
              <Link to="/"><img src="/images/logo.png" alt="Chattak logo" className="nav-logo" height="40" onError={e => { e.target.style.display='none'; }} /></Link>
            </div>
            <Link to="/"><div className="logo-text">Chattak</div></Link>
          </div>
          <div className="nav-links">{companyNavLinks}</div>
        </div>
      </nav>

      {/* HERO */}
      <section className="company-hero">
        <div className="company-hero-video-bg">
          <video className="company-hero-video" autoPlay muted loop playsInline preload="metadata" poster="/video/jam_minaret_poster.png">
            <source src="/video/jam_minaret.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="company-hero-inner">
          <div className="company-hero-content">
            <div className="company-hero-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Our Company
            </div>
            <h1>Building the future of money for <em className="js-text-underline" data-underline="sm">Afghanistan</em></h1>
            <p className="company-hero-lead">We are a team of builders, dreamers, and financial inclusion advocates working to give every Afghan access to safe, fast, and modern digital payments — regardless of where they live.</p>
            <div className="hero-actions">
              <a href="#join" className="m-button__wrap" data-btn="up">
                <span className="m-b-bg" aria-hidden="true"></span>
                <span className="m-b-text" aria-hidden="true">
                  <span className="m-b-text-static">Join the team</span>
                  <span className="m-b-text-up">Join the team</span>
                </span>
                <span className="m-b-arrow" aria-hidden="true">
                  <span className="m-b-arrow-static"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                  <span className="m-b-arrow-up"><img src="/icons/right-arrow.svg" alt="" onError={e => e.target.parentElement.textContent = '→'} /></span>
                </span>
                <span className="sr-only">Join the team</span>
              </a>
            </div>
          </div>

          <div className="hero-stats-cluster">
            <div className="stat-float stat-float-1">
              <div className="stat-float-label">Founded</div>
              <div className="stat-float-value"><span>2025</span></div>
              <div className="stat-float-sub">Kabul, Afghanistan</div>
            </div>
            <div className="stat-float stat-float-2">
              <div className="stat-float-label">Status</div>
              <div className="stat-float-value" style={{ fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>
                <span className="stat-float-dot"></span> Licensing
              </div>
              <div className="stat-float-sub">License in process with the Da Afghanistan Bank</div>
            </div>
            <div className="stat-float stat-float-3">
              <div className="stat-float-label">Our Mission</div>
              <div className="stat-float-value" style={{ fontSize: '1.1rem', lineHeight: 1.3, color: 'var(--dark-bg)' }}>Financial access for every Afghan</div>
              <div className="stat-float-sub" style={{ marginTop: '.6rem' }}>Borderless. Instant. Secure.</div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="section-mission">
        <div className="mission-inner">
          <div>
            <span className="mission-tag">Our Mission</span>
            <h2>Finance that <em>works for everyone</em>, not just a few</h2>
            <p>Afghanistan has over 40 million people — yet the vast majority remain excluded from the formal financial system. Cash is still king, cross-border remittances are expensive and slow, and merchants lack the digital rails to grow their businesses.</p>
            <p>Chattak was founded to change that. We believe technology can eliminate the distance between a shopkeeper in Herat and a customer in Kabul, or between a family in Frankfurt and their relatives in Kandahar.</p>
          </div>
          <div className="mission-pillars">
            {[
              { title: 'Security First', desc: "Every transaction is protected by bank-grade encryption and real-time fraud monitoring. Your money is always safe with Chattak.", icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/> },
              { title: 'Instant Everything', desc: "Payments, transfers, and settlements happen in seconds — not hours or days. We built our stack from scratch for speed.", icon: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></> },
              { title: 'Built for Everyone', desc: "Designed for merchants, individuals, NGOs, and developers alike. Whether you have a smartphone or a basic handset, Chattak will work for you.", icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></> },
            ].map(({ title, desc, icon }) => (
              <div key={title} className="pillar-card" data-animate>
                <div className="pillar-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="var(--dark-bg)" strokeWidth="2">{icon}</svg>
                </div>
                <div className="pillar-text"><h4>{title}</h4><p>{desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-values">
        <div className="values-header">
          <span className="section-tag">What We Stand For</span>
          <h2>Our <span className="highlight js-text-underline" data-underline="sm">values</span> shape every decision<br /> we make</h2>
        </div>
        <div className="values-grid">
          {[
            { title: 'Radical Transparency', desc: 'No hidden fees. No fine print surprises. We communicate clearly with our users, our partners, and each other.', icon: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></> },
            { title: 'Excellence in Craft', desc: "We sweat the small things. Great products are built one thoughtful decision at a time, and we never ship something we're not proud of.", icon: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/> },
            { title: 'People Before Profit', desc: 'We measure our success by the lives we improve — not just the transactions we process. Inclusion is not a side effect; it is the mission.', icon: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/> },
            { title: 'Move With Speed', desc: 'We operate with urgency. The need for financial access in Afghanistan is real and pressing, and we build accordingly.', icon: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></> },
            { title: 'Think Global, Serve Local', desc: 'We build world-class infrastructure tailored to the realities of the Afghan market — offline-capable, low-bandwidth, multi-lingual.', icon: <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></> },
            { title: 'Open by Default', desc: 'We believe in an open API ecosystem where developers and partners can build on our rails to create new financial experiences.', icon: <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/> },
          ].map(({ title, desc, icon }) => (
            <div key={title} className="value-card">
              <div className="value-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="section-story">
        <div className="story-inner">
          <div className="story-left">
            <span className="section-tag">Our Story</span>
            <h2>From an idea in a Kabul café to <em className="js-text-underline" data-underline="sm">Afghanistan's</em> first real e-money platform</h2>
            <p>Chattak started with a simple question: why can't sending money in Afghanistan be as easy as sending a text message? That question led our founders on a years-long journey through regulatory frameworks, infrastructure constraints, and hundreds of conversations with everyday Afghans.</p>
          </div>
          <div className="timeline">
            {[
              { num: 1, year: '2024', title: 'The Spark', desc: "Two co-founders — united by a shared frustration with Afghanistan's cash-only economy — begin researching the landscape of mobile money in emerging markets and decide to build something purpose-built for Afghanistan." },
              { num: 2, year: '2025', title: 'Building the Foundation', desc: "The core engineering team is assembled and begins building the payments infrastructure, with the aim to integrate with Afghanistan's central banking system and establishing early conversations with Da Afghanistan Bank for regulatory approval." },
              { num: 3, year: '2025', title: 'Chattak is Born', desc: 'Official application is submitted and under review by Da Afghanistan Bank. The MVP development is at full steam.' },
              { num: 4, year: '2026 →', title: 'Scaling for Everyone', desc: "We open our waitlist for customer, consumers, and developers, preparing our merchant dashboard, incorporating bank integrations, and begin building towards becoming the payment rails that power Afghanistan's digital economy." },
            ].map(({ num, year, title, desc }) => (
              <div key={num} className="timeline-item" data-animate>
                <div className="timeline-dot">{num}</div>
                <div className="timeline-content">
                  <div className="timeline-year">{year}</div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section-team">
        <div className="team-header">
          <span className="section-tag">The Team</span>
          <h2>The people building tomorrow's <span className="highlight js-text-underline" data-underline="sm">Afghanistan</span></h2>
          <p>We're a small, focused team with deep roots in fintech, software engineering, and the Afghan community.</p>
        </div>
        <div className="team-grid">
          {[
            { initials: 'JE', role: 'Co-founder', name: 'J. E.', gradient: 'linear-gradient(135deg, #713ea5, #9259d4)', bio: 'Deep technical leadership to Chattak. He has held senior roles at multinational firms in the finance and airline sectors, and has co-founded and served as CTO for technology startups in the logistics and healthcare industries.' },
            { initials: 'AM', role: 'Co-founder', name: 'A. M.', gradient: 'linear-gradient(135deg, #e1a536, #c48a1e)', bio: 'Successful entrepreneur combining hands-on leadership with a deep interest in fintech innovation.' },
            { initials: 'MK', role: 'Backend Engineer', name: 'M. K.', gradient: 'linear-gradient(135deg, #537c7a, #3d6260)', bio: 'Backend infrastructure engineer who previously scaled payments at a top European neobank. MK designed Chattak\'s entire transaction processing layer.' },
            { initials: 'SS', role: 'CEO', name: 'S. S.', gradient: 'linear-gradient(135deg, #4a90b8, #2d6a8f)', bio: 'Finance leader with experience across Afghanistan and the UAE. Brings around 10 years managing finances at multiple levels and across different institutions, with a focus on disciplined governance and sustainable growth.' },
          ].map(({ initials, role, name, gradient, bio }) => (
            <div key={initials} className="team-card" data-animate>
              <div className="team-avatar" style={{ background: gradient }}>{initials}</div>
              <h3>{name}</h3>
              <p className="team-role">{role}</p>
              <p className="team-bio">{bio}</p>
              <div className="team-social">
                <a href="#" aria-label="LinkedIn">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      <NebulaCTA onOpenWaitlist={() => setWaitlistOpen(true)} />
      <Footer />
      <WaitlistModal isOpen={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </>
  );
}
